/* eslint-disable max-lines */
import type { BidStatus } from "common-types";

import {
  // sendDeclinedBid,
  sendLateBid,
  sendBidReceived,
  sendUnknownBid,
  sendRejectedBid,
  sendErrorBid,
  sendDeclinedBid,
} from "./helpers/bidHelpers";
import type { AdNodeManager } from "../../core/AdNodeManager";
import type { SlotName } from "../../config/platforms/types";
import { getBooleanQueryParam } from "utils";

type BidId = string;

interface PendingAuction {
  /**
   * Tracks the state of each bid from request to receipt, so that
   * we can account for all the bids.
   */
  bidState: Record<BidId, BidStatus>;
}

/**
 * This plugin provides tracking data for every bid we request, whether or not we
 * successfully receive a bid back. This lets us track the behavior of bidders
 * so we can adjust timeouts, drop bidders that don't bid often enough or high enough,
 * and other analysis of our auctions.
 *
 * Most of the data we need is available in the AuctionDetails that is
 * sent as part of the `auctionEnd` event from Prebid. However, it has some problems:
 *
 * - the noBid it does not differentiate "declined to bid" from "timed out".
 * - some bidders (e.g. rubicon) do not reliably show up in the "auctionDetails.noBids" in
 *   some circumstances (such as when they are late)
 *
 * Therefore, we take a more rigorous approach to accounting for all requested bids.
 * - Track all bids requested on auctionInit
 * - Record late bids from the bidTimeout event
 * - Record all other bid states from the auctionEnd
 * - Send any missed events as unknown
 *
 * 
 * Example usage:
 * 
 * hooks: Hooks = {
    onBiddersInit: (adEngine: AdEngine) => {
      this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback((pbjs: Prebid) => {
        this.auctionTracker = new AuctionTracker(pbjs);
      });
    },
  };

  @throws Error if percentSampleTarget or percentFullAuctions > 1 or < 0 
  @throws Error if percentFullAuctions is not less than percentSampleTarget
 */

interface AuctionTrackerConfig {
  /**
   * Time to wait after the auctionEnd event to make sure the
   * bidTimeout race condition resolves.
   */
  auctionEndWait: number;

  /**
   * The percent of pageviews that should be randomly sampled to send all of their auction data
   *
   * Sending entire auctions is ideal from a data querying perspective, as it allows us to group
   * by auctionId to determine things like who won the auction and what would have happened to that
   * auction with different timeout settings or price floors. But, we are concerned that the volume
   * of GET requests required by the current Beacon infrastructure means collecting full auctions
   * will have a small adverse impact on ad load time. Therefore we are implementing a hybrid sampling
   * strategy, to give us some full auction data and some additional randomly sampled events.
   *
   * Should be a value between 0 and 1
   */
  percentFullAuctions: number;
  /**
   * If we have randomly chosen this page to NOT sample the whole auction, we will instead randomly sample
   * individual records. This gives us more data to use for things like average bid
   * prices by bidder or average response rate by bidder, which do not depend on which auction it came from.
   *
   * This is the target percentage of all records we want to end up with in our sample data, including the
   * records sampled as full auction records. We will use this to calculate how many of records should be
   * sent from pageviews that are not reporting their full auction.
   *
   * Should be a value between 0 and 1, and should be less than the percentFullAuctions.
   */
  percentSampleTarget: number;
}

const DEFAULT_AUCTION_CONFIG: AuctionTrackerConfig = {
  auctionEndWait: 100,
  percentFullAuctions: 0.01,
  percentSampleTarget: 0.1,
};
export class AuctionTracker {
  private readonly pbjs: Prebid;
  private readonly adNodeManager: AdNodeManager;

  private readonly config: AuctionTrackerConfig;

  /**
   * Some downstream use cases benefit from having all records from an auction, while
   * others are OK with sampled data for doing things like average bid amount.
   *
   * This data flag signifies the sampling strategy we are using. This allows us to use
   * more complex sampling strategies - for example sending all "on_time" events, but sampling
   * our "declined" events.
   *
   * true: all records from bids requested were sent for this auction
   * false: only some of the records were sent, so `group by auction_id` will not make sense
   */
  private readonly fullAuction: boolean;

  /**
   * This is the actual sample rate we apply to when selecting which bid records to send.
   *
   * When the pageview is selected as a full auction, it is set to 100%
   * When the pageview is not selected as a full auction, we calculate the value based on
   * the percentFullAuctions and percentSampleTarget.
   */
  private readonly sampleRate: number;

  private pendingAuctions: Record<AuctionId, PendingAuction> = {};

  constructor(
    pbjs: Prebid,
    adNodeManager: AdNodeManager,
    config: AuctionTrackerConfig = DEFAULT_AUCTION_CONFIG,
  ) {
    this.pbjs = pbjs;
    this.adNodeManager = adNodeManager;
    this.config = config;

    this.validateSampling(
      config.percentFullAuctions,
      config.percentSampleTarget,
    );

    this.fullAuction = Math.random() < config.percentFullAuctions;
    if (getBooleanQueryParam("force_tracking")) {
      this.fullAuction = true;
    }

    if (this.fullAuction == true) {
      this.sampleRate = 1;
    } else {
      // this ensures that across all data collected, we end up with our sample target
      // overallRateTarget = fullAuctionRate × recordSampleRateWhenFull + (1-fullAuctionRate) × recordSampleRateWhenNotFull
      // recordSampleRateWhenFull == 1;
      // solving for recordSampleRate:
      this.sampleRate =
        (config.percentSampleTarget - config.percentFullAuctions) /
        (1 - config.percentFullAuctions);
    }

    this.monitorAuctionInit();
    this.monitorTimedOutRequests();
    this.monitorAuctionEnd();
  }

  private validateSampling(
    percentFullAuctions: number,
    percentSampleTarget: number,
  ): void {
    if (percentFullAuctions > percentSampleTarget) {
      throw Error(
        "percentFullAuctions must be less than the percentSampleTarget",
      );
    }

    if (percentSampleTarget > 1 || percentSampleTarget < 0) {
      throw Error("percentSampleTarget must be between 0 and 1");
    }

    if (percentFullAuctions > 1 || percentFullAuctions < 0) {
      throw Error("percentFullAuctions must be between 0 and 1");
    }
  }

  private bidIsSampled(): boolean {
    return Math.random() < this.sampleRate;
  }

  /**
   * At the start of an auction, instantiate a tracking object to keep track of all the
   * bids we have requested
   */
  monitorAuctionInit() {
    this.pbjs.onEvent("auctionInit", (auctionData: PrebidAuctionDetails) => {
      const auction: PendingAuction = {
        bidState: {},
      };
      auctionData.bidderRequests.forEach((bidRequest) => {
        try {
          // Create a record of all bids we expect to get back
          bidRequest.bids.forEach((bidRequest) => {
            auction.bidState[uniqueBidIdentifier(bidRequest)] = "requested";
          });
        } catch {
          console.warn(
            "Error tracking initial auctionInit state for bidRequest:",
            bidRequest,
          );
        }
      });

      this.pendingAuctions[auctionData.auctionId] = auction;
    });
  }

  monitorAuctionEnd() {
    this.pbjs.onEvent("auctionEnd", (auctionData: PrebidAuctionDetails) => {
      // record all bids received
      this.recordBidsReceived(auctionData);
      this.recordBidsRejected(auctionData);

      // The `bidTimeout` event is recorded separately, and occurs a little bit before
      // the auctionEnd. But, since we're receiving each event and processing it asynchronoushly
      // there's a race condition. Wait a little bit so that we can be sure to process all of the
      // timeout events, before marking everything else as declined or unknown.
      // https://github.com/prebid/Prebid.js/blob/77b620da035d9365d0091beafc268b78a215a9fd/src/auction.js#L200-L232
      setTimeout(() => {
        this.recordNoBids(auctionData);

        // unknown bids still sent - these are either "too_late" or "declined" but can't differentiate
        this.recordUnknownBids(auctionData);

        // cleanup
        delete this.pendingAuctions[auctionData.auctionId];
      }, this.config.auctionEndWait);
    });
  }

  /**
   * See behavior here:
   * https://github.com/prebid/Prebid.js/blob/77b620da035d9365d0091beafc268b78a215a9fd/src/auction.js#L207-L214
   */
  monitorTimedOutRequests() {
    this.pbjs.onEvent("bidTimeout", (timedOutRequests: PrebidBidRequest[]) => {
      timedOutRequests.forEach((bidRequest) => {
        try {
          const auction = this.pendingAuctions[bidRequest.auctionId];
          const node = this.adNodeManager.getAdNodeByName(
            bidRequest.adUnitCode as SlotName,
          );
          auction.bidState[uniqueBidIdentifier(bidRequest)] = "timedout";
          sendLateBid(bidRequest, node, this.fullAuction, this.bidIsSampled());
        } catch {
          console.warn("Error recording bidTimeout:", bidRequest);
          sendErrorBid(this.fullAuction, this.bidIsSampled());
        }
      });
    });
  }

  recordBidsReceived(auctionData: PrebidAuctionDetails) {
    const auction = this.pendingAuctions[auctionData.auctionId];

    auctionData.bidsReceived.forEach((bid) => {
      try {
        const node = this.adNodeManager.getAdNodeByName(
          bid.adUnitCode as SlotName,
        );
        auction.bidState[uniqueBidIdentifier(bid)] = "on_time";
        sendBidReceived(bid, node, this.fullAuction, this.bidIsSampled());
      } catch (e) {
        console.error(e);
        console.warn("Error recording bidReceived:", bid);
        sendErrorBid(this.fullAuction, this.bidIsSampled());
      }
    });
  }

  recordBidsRejected(auctionData: PrebidAuctionDetails) {
    const auction = this.pendingAuctions[auctionData.auctionId];

    auctionData.bidsRejected.forEach((bid) => {
      try {
        auction.bidState[uniqueBidIdentifier(bid)] = "rejected";
        const node = this.adNodeManager.getAdNodeByName(
          bid.adUnitCode as SlotName,
        );
        sendRejectedBid(bid, node, this.fullAuction, this.bidIsSampled());
      } catch {
        console.warn("Error recording bidRejected:", bid);
        sendErrorBid(this.fullAuction, this.bidIsSampled());
      }
    });
  }

  /**
   * When the auction is complete, record all slot+bidders where the bidder
   * signaled they are done responding but did not bid on this slot.
   *
   * NOTE: This method assumes that `this.monitorTimedOutRequests` has been
   * set up to update the auction state with timeouts, and that there's enough time
   * before this method is called to get the `timedout` state into the right spot.
   * If the timedout bids are not properly recorded ahead of time, this will
   * record all the noBids data as `declined`
   *
   * @param auctionData the data provided from the auctionEnd event
   */
  recordNoBids(auctionData: PrebidAuctionDetails) {
    const auction = this.pendingAuctions[auctionData.auctionId];
    auctionData.noBids.forEach((bidRequest) => {
      try {
        const id = uniqueBidIdentifier(bidRequest);

        /**
         * The auction data mixes "timedout" with "declined" in auction.noBids
         * We separately record `timedout` and track them with our state, so
         * if they are not already recorded as timedout they are declined.
         */
        if (auction.bidState[id] != "timedout") {
          auction.bidState[id] = "declined";

          const node = this.adNodeManager.getAdNodeByName(
            bidRequest.adUnitCode as SlotName,
          );

          sendDeclinedBid(
            bidRequest,
            node,
            this.fullAuction,
            this.bidIsSampled(),
          );
        }
      } catch {
        console.warn("Error recording no_bid", bidRequest);
        sendErrorBid(this.fullAuction, this.bidIsSampled());
      }
    });
  }

  /**
   * On auctionInit, we record all slots+bidder combos. On auctionEnd and bidTimeout
   * we record all known states. If any of the original bids were not identifed in one
   * of these events, we send it here as an unknown state.
   *
   * There appear to be some flaws in the responses from some BidderAdapters vs. what
   * the Prebid auction.js assumes will be available, causing some bids to not show up in
   * the auction.noBids; we can't fully distinguish between `timedout` and `declined` in these
   * cases.
   *
   * @param auctionData the data provided from the auctionEnd event
   */
  recordUnknownBids(auctionData: PrebidAuctionDetails) {
    try {
      const allOriginalBids = auctionData.bidderRequests.flatMap(
        (request) => request.bids,
      );

      const auction = this.pendingAuctions[auctionData.auctionId];
      for (const [bidId, state] of Object.entries(auction.bidState)) {
        if (state === "requested") {
          const bidRequest = allOriginalBids.find(
            (bidRequest) => uniqueBidIdentifier(bidRequest) === bidId,
          );
          if (bidRequest) {
            const node = this.adNodeManager.getAdNodeByName(
              bidRequest.adUnitCode as SlotName,
            );
            sendUnknownBid(
              bidRequest,
              node,
              this.fullAuction,
              this.bidIsSampled(),
            );
          } else {
            console.warn("Could not find bid data to report");
            sendErrorBid(this.fullAuction, this.bidIsSampled());
          }
        }
      }
    } catch {
      console.warn("Error recording unknown bids");
    }
  }
}

/**
 * Returns the string that can uniquely identify the same bid before
 * and after the auction.
 *
 * Per the docs, the BidId is supposed to do this; it is available
 * as bidId in the PrebidBidRequest, and as requestId in the PrebidBidResponse. |
 * https://docs.prebid.org/dev-docs/bidder-adaptor.html#interpreting-the-response
 *
 * However, in the real world requestId is sometimes null (e.g. cpmstar) so cannot
 * be used to reliably track bids before and after they are received. Concatenation
 * of bidder and transactionId is used instead and should also uniquely identify bids.
 */
export function uniqueBidIdentifier(bid: PrebidBidRequest | PrebidBidResponse) {
  return `${bid.bidder}-${bid.transactionId}`;
}
